// Clone settings (sClone) — encrypted Samsung Tizen clone bundles that make up
// a GLOBAL catalog (keyed by model + firmware version only; there is no
// project scope). Managed through /sclones.json: GET lists, POST (multipart)
// uploads, DELETE removes. See the sclone-api spec for the wire contract.
//
// Unlike most features this has no reducer: the catalog is small and is
// refetched after every mutation, so it lives in the page's local state
// rather than Redux (mirroring the local-users helpers). This module only
// owns the shared shape + endpoint.

// The Casting setting a bundle is for. A number, not a flag: more may come.
export type ScloneCasting = number;
export const SCLONE_CASTING_OFF = 0;
export const SCLONE_CASTING_ON  = 1;

// One clone-settings bundle. Returned by the list (as an array) and by a
// successful upload (single, in errors[0].result).
export interface ScloneEntry {
  // Canonical (lowercased) model, from ProductCloneInfo.txt line 2.
  model:       string;
  // Canonical (lowercased) firmware version, from line 1.
  version:     string;
  // 0 = Casting OFF, 1 = Casting ON, null = serves both settings (bundles
  // uploaded before casting existed, or without it). Any other number is a
  // setting added later, so never treat this as a boolean.
  casting:     ScloneCasting | null;
  // Download path for the bundle. OPAQUE — use verbatim; it may become an
  // absolute, pre-signed URL when the backend moves to a cloud blob store.
  url:         string;
  // Bundle size in bytes.
  size:        number;
  // Last-modified Unix timestamp (seconds).
  updated_at:  number;
}

// Translation key (+ value) for a casting: ON, OFF, Both (null, or missing on
// an older backend that has no casting member) or the bare number.
export function scloneCastingLabel(casting: ScloneCasting | null | undefined): { key: string; value?: string } {
  if (casting === null || casting === undefined) return { key: "clone_settings:casting:both" };
  if (casting === SCLONE_CASTING_ON)  return { key: "clone_settings:casting:on" };
  if (casting === SCLONE_CASTING_OFF) return { key: "clone_settings:casting:off" };
  return { key: "clone_settings:casting:other", value: String(casting) };
}

// Delete query string. A "Both" entry (casting null) leaves casting out —
// that is how the server addresses it; anything else sends it as listed.
export function scloneDeleteQuery(model: string, version: string, casting: ScloneCasting | null | undefined): string {
  let q = `?model=${encodeURIComponent(model)}&version=${encodeURIComponent(version)}`;
  if (casting !== null && casting !== undefined) q += `&casting=${encodeURIComponent(String(casting))}`;
  return q;
}

// An older middleware (before clone casting) lists entries WITHOUT a casting
// member and ignores casting on upload, so ON and OFF would overwrite each
// other. Only decidable once the catalog has at least one entry.
export function isLegacySclonesBackend(entries: ScloneEntry[]): boolean {
  return entries.length > 0 && entries.every((e) => !("casting" in e));
}

export const sclonesUrl = "/sclones.json";

// Whole-request cap the backend enforces (spec §5, 64 MiB). Guarding
// client-side lets an oversized file fail fast with a friendly message
// instead of a 413.
export const MAX_UPLOAD_BYTES = 64 * 1024 * 1024;

// Human-readable byte size (e.g. "647.4 KB", "20.0 MB"). Used for the grid's
// size column and the live upload counter.
export function formatBytes(n: number): string {
  if (!n || n < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = n, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
}
